import { useEffect, useState } from 'react';
import { LoginModal } from './LoginModal';
import { RegisterModal } from './RegisterModal';
import { ForgotPasswordModal } from './ForgotPasswordModal';

/**
 * Listens for `window`-level `kss:open-login`, `kss:open-register`,
 * and `kss:open-forgot-password` events (dispatched by `WebHeader`,
 * `ShopList`, the toggle links inside the modals themselves, etc.)
 * and shows the matching modal. The optional `detail.redirect` field
 * is forwarded so "Open Panel" / "Checkout" / etc. send the user to
 * the right page after sign-in / sign-up. The forgot-password event
 * also accepts `detail.email` so the address typed on the login
 * screen is carried over.
 *
 * Only one modal is open at a time — the toggle links between them
 * close the source modal and open the target via the same event
 * channel, which keeps the modal state in one place.
 */
export function GlobalLoginModalHost() {
  const [mode, setMode] = useState<'closed' | 'login' | 'register' | 'forgot'>('closed');
  const [redirect, setRedirect] = useState<string | undefined>(undefined);
  const [forgotEmail, setForgotEmail] = useState<string | undefined>(undefined);

  useEffect(() => {
    function openLogin(e: Event) {
      const detail = (e as CustomEvent<{ redirect?: string }>).detail;
      setRedirect(detail?.redirect);
      setMode('login');
    }
    function openRegister(e: Event) {
      const detail = (e as CustomEvent<{ redirect?: string }>).detail;
      setRedirect(detail?.redirect);
      setMode('register');
    }
    function openForgot(e: Event) {
      const detail = (e as CustomEvent<{ email?: string }>).detail;
      setForgotEmail(detail?.email);
      setMode('forgot');
    }
    window.addEventListener('kss:open-login', openLogin);
    window.addEventListener('kss:open-register', openRegister);
    window.addEventListener('kss:open-forgot-password', openForgot);
    return () => {
      window.removeEventListener('kss:open-login', openLogin);
      window.removeEventListener('kss:open-register', openRegister);
      window.removeEventListener('kss:open-forgot-password', openForgot);
    };
  }, []);

  return (
    <>
      <LoginModal
        open={mode === 'login'}
        onClose={() => setMode('closed')}
        forceRedirect={redirect}
        onSwitchToRegister={() =>
          window.dispatchEvent(new CustomEvent('kss:open-register', { detail: { redirect } }))
        }
        onSwitchToForgotPassword={(email) =>
          window.dispatchEvent(
            new CustomEvent('kss:open-forgot-password', { detail: { email } }),
          )
        }
      />
      <RegisterModal
        open={mode === 'register'}
        onClose={() => setMode('closed')}
        forceRedirect={redirect}
        onSwitchToLogin={() =>
          window.dispatchEvent(new CustomEvent('kss:open-login', { detail: { redirect } }))
        }
      />
      <ForgotPasswordModal
        open={mode === 'forgot'}
        onClose={() => setMode('closed')}
        initialEmail={forgotEmail}
        onSwitchToLogin={() =>
          window.dispatchEvent(new CustomEvent('kss:open-login', { detail: { redirect } }))
        }
      />
    </>
  );
}
